import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';

const ENTITY_TYPE_LABELS = {
  truck_shipment: 'Truck Shipment',
  ocean_import: 'Ocean Import',
  ocean_export: 'Ocean Export',
  air_import: 'Air Import',
  air_export: 'Air Export',
  misc: 'Misc Shipment',
  charge: 'Charge',
  payment: 'Payment',
  trade_partner: 'Trade Partner',
  user: 'User',
};

const ENTITY_TYPE_ROUTES = {
  truck_shipment: '/truck',
  ocean_import: '/ocean-import',
  ocean_export: '/ocean-export',
  air_import: '/air-import',
  air_export: '/air-export',
  misc: '/misc',
  charge: '/charge',
  payment: '/payment',
  trade_partner: '/trade-partner',
};

const ACTION_STYLES = {
  create: { background: '#dcfce7', color: '#059669' },
  update: { background: '#dbeafe', color: '#2563eb' },
  delete: { background: '#fee2e2', color: '#dc2626' },
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleString();
}

function formatDetails(detailsStr) {
  if (!detailsStr) return '-';
  try {
    const obj = JSON.parse(detailsStr);
    return Object.entries(obj)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${k}: ${v}`)
      .join(', ');
  } catch {
    return detailsStr;
  }
}

const ActivityLog = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', '50');
      if (entityTypeFilter) params.append('entity_type', entityTypeFilter);
      if (actionFilter) params.append('action', actionFilter);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      const res = await api.get(`/activity-log?${params}`);
      setLogs(res.data.data || []);
      setTotalPages(res.data.pages || 1);
    } catch (error) {
      toast.error('Failed to load activity log');
    } finally {
      setLoading(false);
    }
  }, [page, entityTypeFilter, actionFilter, fromDate, toDate]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(loadLogs, 30000);
    return () => clearInterval(interval);
  }, [loadLogs]);

  const handleRefClick = (log) => {
    const route = ENTITY_TYPE_ROUTES[log.entity_type];
    if (route && log.entity_id) {
      navigate(`${route}/${log.entity_id}`);
    }
  };

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">ACTIVITY LOG</h2>
        </div>

        <div className="filter-bar" style={{ flexWrap: 'wrap' }}>
          <div className="filter-group">
            <label className="filter-label">Type:</label>
            <select className="filter-select" value={entityTypeFilter} onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(1); }}>
              <option value="">All</option>
              {Object.entries(ENTITY_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Action:</label>
            <select className="filter-select" value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}>
              <option value="">All</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">From:</label>
            <input type="date" className="filter-input" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(1); }} style={{ width: 150 }} />
          </div>
          <div className="filter-group">
            <label className="filter-label">To:</label>
            <input type="date" className="filter-input" value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(1); }} style={{ width: 150 }} />
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Action</th>
                <th>Type</th>
                <th>Reference</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                    <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                    No activity found
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id}>
                    <td title={formatDate(log.created_at)} style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
                      {timeAgo(log.created_at)}
                    </td>
                    <td style={{ fontWeight: 500 }}>{log.username || '-'}</td>
                    <td>
                      <span style={{
                        padding: '2px 10px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        ...(ACTION_STYLES[log.action] || {}),
                      }}>
                        {log.action}
                      </span>
                    </td>
                    <td style={{ fontSize: '13px' }}>
                      {ENTITY_TYPE_LABELS[log.entity_type] || log.entity_type}
                    </td>
                    <td>
                      {log.entity_ref ? (
                        <span
                          style={{ color: '#0891b2', cursor: 'pointer', fontWeight: 500 }}
                          onClick={() => handleRefClick(log)}
                        >
                          {log.entity_ref}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>#{log.entity_id || '-'}</span>
                      )}
                    </td>
                    <td style={{ fontSize: '12px', color: '#64748b', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {formatDetails(log.details)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '16px' }}>
            <button
              className="btn btn-sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <span style={{ fontSize: '13px', color: '#64748b' }}>
              Page {page} of {totalPages}
            </span>
            <button
              className="btn btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLog;
